/**
 * Winner banner + post-match damage meter (#471). Shown at end of match.
 *
 * Two buttons:
 *   - "Re-queue" (`data-testid="requeue"`) — the ORIGINAL "Play again"
 *     behavior, unchanged, just relabeled: leave + return to Home, where
 *     the player manually starts a fresh create/quick-join (Home is the
 *     canonical create surface; we deliberately do NOT pre-create here —
 *     see the handler comment below).
 *   - "Leave" (`data-testid="leave"`) — a genuinely separate action that
 *     also returns to Home with no new match created. Distinct handler
 *     (not a shared alias) so the two are free to diverge later (e.g. a
 *     future instant-requeue) without an entangled call site.
 *
 * WP8 relies on the `data-winner` attribute.
 */

import type { CSSProperties, ReactElement } from "react";
import { useStore } from "../store/context.js";
import { navigate } from "../router/hashRoute.js";
import { buildDamageMeterRows } from "./damageMeter.js";

export function WinnerBanner(): ReactElement {
  const { state, dispatch } = useStore();
  const winner = state.phase.winner;
  const ownSeat = state.ownSeat;

  let label: string;
  let bannerKind: "win" | "loss" | "draw";
  if (winner === null) {
    label = "Draw";
    bannerKind = "draw";
  } else if (ownSeat !== null && Number(winner) === Number(ownSeat)) {
    label = "You win";
    bannerKind = "win";
  } else {
    label = "You lose";
    bannerKind = "loss";
  }

  const rows = buildDamageMeterRows(state.roster, state.damageDealt, ownSeat);

  return (
    <div
      data-testid="winner-banner"
      data-winner={bannerKind}
      data-phase="ended"
      style={{
        position: "absolute",
        top: "40%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        padding: "24px 48px",
        background:
          bannerKind === "win"
            ? "#1a5a3d"
            : bannerKind === "loss"
              ? "#5a1a1a"
              : "#333",
        borderRadius: 8,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 16,
        color: "#fff",
      }}
    >
      <h1 style={{ margin: 0 }}>{label}</h1>

      {rows.length > 0 ? (
        <table data-testid="damage-meter" style={{ borderCollapse: "collapse", minWidth: 280 }}>
          <thead>
            <tr>
              <th style={headerCellStyle}>Player</th>
              <th style={headerCellStyle}>Champion</th>
              <th style={{ ...headerCellStyle, textAlign: "right" }}>Damage</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.seat} data-testid="damage-meter-row" data-seat={row.seat}>
                <td style={cellStyle}>{row.isOwnSeat ? "You" : `Seat ${String(row.seat)}`}</td>
                <td style={cellStyle}>{row.championName}</td>
                <td style={{ ...cellStyle, textAlign: "right" }}>{row.damageDealt}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      <div style={{ display: "flex", gap: 12 }}>
        <button
          type="button"
          data-testid="requeue"
          onClick={() => {
            // Clear the ended match's identity, then return to Home. Home is
            // the canonical create surface (its create-effect handles the
            // navigate-to-match hop on the fresh server round-trip). We do
            // NOT pre-create here: leaving a dangling created room that the
            // user then abandons is exactly the stale-room bug we're fixing.
            dispatch({ type: "match.leave" });
            navigate({ kind: "home" });
          }}
          style={{
            padding: "8px 20px",
            background: "#3aa675",
            color: "#fff",
            border: "none",
            borderRadius: 4,
          }}
        >
          Re-queue
        </button>
        <button
          type="button"
          data-testid="leave"
          onClick={() => {
            // Genuinely distinct from "Re-queue" (#471): also returns to
            // Home, but never fires a createMatch/quickJoin — this is just
            // "leave", full stop.
            dispatch({ type: "match.leave" });
            navigate({ kind: "home" });
          }}
          style={{
            padding: "8px 20px",
            background: "#444",
            color: "#fff",
            border: "none",
            borderRadius: 4,
          }}
        >
          Leave
        </button>
      </div>
    </div>
  );
}

const headerCellStyle: CSSProperties = {
  textAlign: "left",
  padding: "4px 12px",
  borderBottom: "1px solid #666",
  fontWeight: 600,
};

const cellStyle: CSSProperties = {
  padding: "4px 12px",
};
